import addDays from "date-fns/addDays";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import parse from "date-fns/parse";
import startOfDay from "date-fns/startOfDay";
import get from "lodash/get";
import isEqual from "lodash/isEqual";

import { validateFormattedStringAsDate } from "./date";
import { parseDecimalField } from "./decimal";
import { datePickerFormFormat } from "./formats";
import { isDateFormattedRegex, isEmailRegex, isPhoneNumberRegex } from "./regexes";

export const buildValidator =
  <T>(predicate: (value: T, values: Record<string, any>) => boolean, errorMessage: string) =>
  (value: T, values: Record<string, any>) =>
    predicate(value, values) ? undefined : errorMessage;

export const optionalCombineValidators =
  (...validators: any[]) =>
  <T>(value: T, values: Record<string, any>) =>
    value
      ? validators.reduce(
          (errorMessage, validator) => errorMessage || validator(value, values),
          undefined
        )
      : undefined;

export const combineValidators =
  (...validators: any[]) =>
  <T>(value: T, values: Record<string, any>) =>
    validators.reduce(
      (errorMessage, validator) => errorMessage || validator(value, values),
      undefined
    );

export const isEmail = (errorMessage: string) =>
  buildValidator<string>(value => isEmailRegex.test((value || "").toLowerCase()), errorMessage);

export const minLength = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => value.length >= threshold, errorMessage);

export const equalToField = (field: string, errorMessage: string) =>
  buildValidator((value, values) => value === values[field], errorMessage);

export const notEqualToValue = (forbiddenValue: string | number, errorMessage: string) =>
  buildValidator(value => value !== forbiddenValue, errorMessage);

export const maxLength = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => value.length <= threshold, errorMessage);

export const hasLength = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => value.length === threshold, errorMessage);

export const notEmpty = (errorMessage: string) =>
  buildValidator<string>(value => Boolean(value.length), errorMessage);

export const required = (errorMessage: string) =>
  buildValidator(
    value => typeof value === "boolean" || typeof value === "number" || !!value,
    errorMessage
  );

export const upperBound = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => {
    const parsed = typeof value === "number" ? value : parseInt(value, 10);
    return Boolean((parsed || parsed === 0) && parsed <= threshold);
  }, errorMessage);

export const lowerBound = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => {
    const parsed = typeof value === "number" ? value : parseInt(value, 10);
    return Boolean((parsed || parsed === 0) && parsed >= threshold);
  }, errorMessage);

export const isPositive = (errorMessage: string) =>
  buildValidator<string>(value => parseFloat(value) > 0, errorMessage);

export const isTrue = (errorMessage: string) =>
  buildValidator(value => Boolean(value) === true, errorMessage);

export const isFutureDate = (errorMessage: string) =>
  buildValidator<string>(value => {
    const today = startOfDay(new Date());
    const selectedDate = startOfDay(parse(value, datePickerFormFormat, today));

    return isAfter(selectedDate, today);
  }, errorMessage);

export const isNotFutureDate = (errorMessage: string) =>
  buildValidator<string>(value => {
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const selectedDate = startOfDay(parse(value, datePickerFormFormat, tomorrow));

    return isBefore(selectedDate, tomorrow);
  }, errorMessage);

export const isPhoneNumber = (errorMessage: string) =>
  buildValidator<string>(value => isPhoneNumberRegex.test(value), errorMessage);

export const isPassword = (isPasswordRegex: RegExp, errorMessage: string) =>
  buildValidator<string>(value => isPasswordRegex.test(value), errorMessage);

export const isDateFormatted = (errorMessage: string) =>
  buildValidator<string>(value => isDateFormattedRegex.test(value), errorMessage);

export const isDateValid = (errorMessage: string) =>
  buildValidator<string>(value => validateFormattedStringAsDate(value), errorMessage);

export const isDateInCorrectRange = (
  lowerThreshold: Date,
  upperThreshold: Date,
  errorMessage: string
) =>
  buildValidator<string>(value => {
    const parsedDateString = parse(value, "dd.MM.yyyy", new Date());

    return (
      !isBefore(parsedDateString, lowerThreshold) && !isAfter(parsedDateString, upperThreshold)
    );
  }, errorMessage);

export const hasValue = (expectedValue: number, errorMessage: string) =>
  buildValidator(value => isEqual(value, expectedValue), errorMessage);

export const includesPurposesWithValue = (expectedValue: string, errorMessage: string) =>
  buildValidator(
    (value: string) => !(value.includes(expectedValue) && value.length > 1),
    errorMessage
  );

export const isFilledAndIsRelatedFieldFilled = (field: string, errorMessage: string) =>
  buildValidator((value: any, values: any) => get(values, field) && value, errorMessage);

export const isAnOptionFrom = (options: any[], errorMessage: string) =>
  buildValidator((value: any) => options.map(option => option.value).includes(value), errorMessage);

export const combineWithPrefillCheck =
  (requiredErrorMessage: string, ...validators: any[]) =>
  (value: any, values: any, meta: any) => {
    if (!meta.initial) {
      return optionalCombineValidators(...validators)(value, values);
    }
    return combineValidators(required(requiredErrorMessage), ...validators)(value, values);
  };

export const upperDecimalBound = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => {
    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return Boolean((parsed || parsed === 0) && parsed <= threshold);
  }, errorMessage);

export const upperDecimalExcludedBound = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => {
    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return Boolean((parsed || parsed === 0) && parsed < threshold);
  }, errorMessage);

export const lowerDecimalBound = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => {
    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return Boolean((parsed || parsed === 0) && parsed >= threshold);
  }, errorMessage);

export const lowerDecimalBoundWithUndefined = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => {
    if (value === undefined || value === "") return true;

    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return Boolean((parsed || parsed === 0) && parsed >= threshold);
  }, errorMessage);

export const upperDecimalBoundWithUndefined = (threshold: number, errorMessage: string) =>
  buildValidator<string>(value => {
    if (value === undefined || value === "") return true;

    const parsed = typeof value === "number" ? value : parseDecimalField(value);
    return Boolean((parsed || parsed === 0) && parsed <= threshold);
  }, errorMessage);

export const excludeSelectValues = (excludingMap: Map<any, any>, errorMessage: string) =>
  buildValidator(value => !excludingMap.has(value), errorMessage);
